import { For, Show } from "solid-js";
import { TIERS } from "system";
import { TierBadge } from "~/components/tier-badge";
import { Button } from "~/components/ui/button";
import { useAuth } from "~/lib/auth";
import { headerLogoUrl, kanjiUrl } from "~/lib/svg-assets";

import { createFileRoute, useNavigate } from "@tanstack/solid-router";

export const Route = createFileRoute("/join")({
  component: JoinPage,
});

const SLASH_STYLE = {
  background:
    "repeating-linear-gradient(-45deg,transparent,transparent 60px,var(--color-brand) 60px,var(--color-brand) 61px)",
} as const;

function JoinPage() {
  const { state, refreshUser } = useAuth();
  const navigate = useNavigate();

  async function handleRetry() {
    await refreshUser();
    if (!state.requiresJoin && state.user) {
      navigate({ to: "/dashboard" });
    }
  }

  return (
    <div class="relative flex items-center justify-center px-4 py-12 overflow-hidden">
      {/* Diagonal slash backdrop */}
      <div
        class="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={SLASH_STYLE}
      />

      {/* Corner brackets */}
      <div class="fixed top-4 left-4 w-10 h-10 sm:w-16 sm:h-16 border-t-2 border-l-2 border-brand/40 opacity-60 pointer-events-none" />
      <div class="fixed top-4 right-4 w-10 h-10 sm:w-16 sm:h-16 border-t-2 border-r-2 border-brand/40 opacity-60 pointer-events-none" />
      <div class="fixed bottom-4 left-4 w-10 h-10 sm:w-16 sm:h-16 border-b-2 border-l-2 border-brand/40 opacity-60 pointer-events-none" />
      <div class="fixed bottom-4 right-4 w-10 h-10 sm:w-16 sm:h-16 border-b-2 border-r-2 border-brand/40 opacity-60 pointer-events-none" />

      {/* Redline glow at top */}
      <div class="fixed top-0 left-0 right-0 h-[2px] bg-brand animate-redline-glow pointer-events-none" />

      <div class="relative max-w-lg w-full text-center space-y-8">
        {/* Logo lockup */}
        <div class="flex flex-col items-center gap-3">
          <img src={kanjiUrl} alt="黒罪" class="h-14 opacity-80" />
          <img src={headerLogoUrl} alt="Kurotsumi CRG" class="h-8 w-auto" />
        </div>

        <div class="space-y-2">
          <p class="text-label-xs text-brand uppercase tracking-widest font-semibold">
            One step away
          </p>
          <h1 class="font-display text-3xl uppercase tracking-display text-text-base">
            Join the server
          </h1>
          <p class="text-text-muted leading-relaxed text-sm max-w-md mx-auto">
            Kurotsumi CRG lives on Discord. The app handles your card, rankings, and duels — the racing happens with the group.
          </p>
        </div>

        <p class="text-text-muted leading-relaxed text-sm max-w-md mx-auto">
          Ten tiers. Earn yours:
        </p>
        {/* Tier strip — a glimpse of what they're joining */}
        <div class="flex flex-wrap justify-center gap-1.5 py-2 border-y border-border-subtle">
          <For each={TIERS}>
            {(tier) => <TierBadge tier={tier} size="sm" />}
          </For>
        </div>

        <div class="space-y-3">
          <Show when={state.inviteUrl}>
            {(url) => (
              <a
                href={url()}
                target="_blank"
                rel="noopener noreferrer"
                class="block"
              >
                <Button size="lg" class="w-full">
                  Join Kurotsumi CRG on Discord
                </Button>
              </a>
            )}
          </Show>

          <Button
            variant="outline"
            size="lg"
            class="w-full"
            onClick={handleRetry}
            disabled={state.loading}
          >
            {state.loading ? "Checking…" : "I've joined — continue"}
          </Button>
        </div>
      </div>
    </div>
  );
}
